import { forwardRef, type ReactNode } from 'react';

import styles from './StepHeading.module.less';

// We use (step - 1) to index these.
const descriptions: readonly [string, string] = [
  'Choose the type of customization',
  'Preview and generate report',
];

type Props = {
  step: 1 | 2;
  children: ReactNode;
};

/**
 * Renders a StepHeading and then renders the children in a section.
 *
 * Uses forwardRef so the parent can attach a ref to the underlying <section>
 * element — this is needed because StepHeading owns the DOM node, so callers
 * can't attach a ref to it directly without forwarding.
 *
 * @param {Props} props - StepHeading props
 * @param {1 | 2} props.step - The step number (1 or 2)
 * @param {ReactNode} props.children - The children to render in the section
 */
export const StepHeading = forwardRef<HTMLElement, Props>(({ step, children }, ref) => {
  return (
    <section ref={ref} className={styles.section}>
      <div className={styles.header}>
        <h2 className={styles.heading}>Step {step}</h2>
        <p className={styles.description}>{descriptions[step - 1]}</p>
      </div>
      {children}
    </section>
  );
});

StepHeading.displayName = 'StepHeading';
